// core/cards.ts - CardIndex encode/decode. ENGINE_DESIGN.md §1.
//
// A CardIndex is 0..51 with `index = rank * 4 + suit`; rank 0=2 .. 12=A, suit 0=c,1=d,2=h,3=s.
// This encoding is byte-identical to @poker-apprentice/hand-evaluator's internal card ids
// (its bundled source: `id = rankIndex * SUIT_COUNT + suitIndex`, ranks 2..A, suits c,d,h,s),
// so eval.ts converts between the two with a single lookup and no re-mapping. The library's
// public API still takes card *strings*, which is the only reason cardToString exists.
import { CardIndexSchema, CardStringSchema, } from '@postflop/schema';
/** Rank characters indexed by Rank (0=2 .. 12=A). */
export const RANK_CHARS = '23456789TJQKA';
/** Suit characters indexed by Suit (0=c, 1=d, 2=h, 3=s). */
export const SUIT_CHARS = 'cdhs';
export const SUIT_COUNT = 4;
export const DECK_SIZE = 52;
/** rank * 4 + suit → CardIndex. Assumes valid rank/suit; use `card()` for untrusted strings. */
export const makeCard = (rank, suit) => (rank * SUIT_COUNT + suit);
/** Rank (0..12) of a card. `c >> 2` == floor(c / 4) since SUIT_COUNT is exactly 4. */
export const rankOf = (c) => (c >> 2);
/** Suit (0..3) of a card. */
export const suitOf = (c) => (c & 3);
/** Validate an arbitrary number into a branded CardIndex (throws if out of 0..51). Boundary use. */
export const toCardIndex = (n) => CardIndexSchema.parse(n);
/** Parse a card string ("As", "Td") → CardIndex. Validates via zod; throws on malformed input. */
export const card = (s) => {
    const str = CardStringSchema.parse(s); // enforces /^[2-9TJQKA][cdhs]$/
    const rank = RANK_CHARS.indexOf(str[0]);
    const suit = SUIT_CHARS.indexOf(str[1]);
    return makeCard(rank, suit);
};
/** CardIndex → card string ("As"). Cheap range check; no zod on this hot path. */
export const cardToString = (c) => {
    const n = c;
    if (!Number.isInteger(n) || n < 0 || n > 51)
        throw new RangeError(`CardIndex out of range: ${n}`);
    return (RANK_CHARS[n >> 2] + SUIT_CHARS[n & 3]);
};
/** Parse a run of card strings ("AsKhQd" or "As Kh Qd" or "As,Kh,Qd") → CardIndex[]. */
export const parseCards = (src) => (src.match(/[2-9TJQKA][cdhs]/g) ?? []).map(card);
/** CardIndex[] → card strings. */
export const cardsToStrings = (cards) => cards.map(cardToString);
//# sourceMappingURL=cards.js.map